import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const ADVISORS = [
  {
    name: "Sakthi Priya Balaji Ranganathan",
    role: "Advisor (India)",
    photo: "/advisors/sakthi-ranganathan.jpg",
    bio: "Sakthi Ranganathan is the Founder and CEO of JIX Reality, an award-winning Christchurch technology studio specialising in artificial intelligence, extended reality and emerging technologies. Originally from India, Sakthi arrived in Aotearoa as an international student on the day of the 2011 Christchurch earthquake and transformed a background in graphic design and a spark from a local hackathon into one of the city's most recognised innovation companies. JIX Reality delivers human-centred technology solutions across government, education, health, retail and the GLAM sector (galleries, libraries, archives and museums), with projects spanning VR, AR, mixed reality, AI and interactive digital experiences. The company was a regional winner of the 2018 NZ Space Challenge for developing an AR-based navigation system for use on ice, and has since received funding for an AI-powered virtual companion project supporting elderly citizens. In early 2025, Sakthi was selected to join Prime Minister Christopher Luxon's official business delegation to New Delhi and Mumbai, a full-circle moment from international student to technology leader strengthening ties between Aotearoa and India. As T.I.I.N.A.'s Advisor (India), Sakthi brings specialist expertise in technology commercialisation, digital transformation, innovation strategy and the growing economic relationship between India and Aotearoa.",
    company: "JIX Reality",
    website: "https://jix.co.nz",
    milestones: [
      { year: "2011", title: "Arrived in Aotearoa", detail: "Landed in Christchurch as an international student from India on the day of the February earthquake" },
      { year: "2015", title: "Hackathon spark", detail: "Won a local hackathon that ignited a passion for emerging technology and creative problem-solving" },
      { year: "2017", title: "Founded JIX Reality", detail: "Established JIX Reality in Christchurch, specialising in VR, AR, AI and interactive digital experiences" },
      { year: "2018", title: "NZ Space Challenge winner", detail: "Regional winner of the NZ Space Challenge for an AR-based navigation system designed for use on ice" },
      { year: "2023", title: "AI virtual companion", detail: "Received funding for an AI-powered virtual companion project supporting elderly citizens" },
      { year: "2025", title: "PM delegation to India", detail: "Selected to join Prime Minister Christopher Luxon's official business delegation to New Delhi and Mumbai" },
      { year: "2026", title: "Joined T.I.I.N.A.", detail: "Appointed as T.I.I.N.A.'s Advisor (India), bridging innovation ecosystems between Aotearoa and India" },
    ],
  },
  {
    name: "Marcello Menoita",
    role: "Advisor (Brazil)",
    photo: "/advisors/marcello-menoita.jpg",
    bio: "Marcello Menoita is the Chief Executive Officer of the New Zealand Brazil Business Chamber and a globally experienced business strategist with over 30 years of international market experience spanning deeptech, space technology, artificial intelligence, energy transmission and trade. A citizen of Brazil, Portugal and New Zealand, Marcello has built a career connecting businesses, governments and innovation ecosystems across continents. He is credited with leading the introduction of the first smartphone into the Brazilian market in 2001 and has since operated at the intersection of emerging technology and international commerce. His current roles include an exclusive territory mandate for Latin America and Portugal at Emrod, a pioneering wireless power transfer company, and CEO of Soul Virtual and Nexus Business International. Marcello has represented New Zealand-Brazil trade interests at the highest levels, including facilitating bilateral agreements between foreign ministers and hosting international delegations at events such as Fieldays. As T.I.I.N.A.'s Advisor (Brazil), Marcello brings specialist expertise in international trade, market entry strategy, deeptech commercialisation and the growing economic relationship between Aotearoa and Latin America.",
    company: "NZ Brazil Business Chamber",
    website: "https://www.linkedin.com/in/marcelo-menoita-585240",
    milestones: [
      { year: "2001", title: "First smartphone in Brazil", detail: "Led the introduction of the first smartphone into the Brazilian market" },
      { year: "2005", title: "International trade career", detail: "Established a career connecting businesses and governments across continents in deeptech and commerce" },
      { year: "2015", title: "NZ Brazil Business Chamber", detail: "Appointed Chief Executive Officer, representing bilateral trade interests at the highest levels" },
      { year: "2019", title: "Fieldays delegations", detail: "Hosted international delegations and facilitated bilateral agreements between foreign ministers" },
      { year: "2022", title: "Emrod partnership", detail: "Secured exclusive territory mandate for Latin America and Portugal at Emrod, a wireless power transfer pioneer" },
      { year: "2024", title: "Soul Virtual and Nexus", detail: "Expanded leadership as CEO of Soul Virtual and Nexus Business International" },
      { year: "2026", title: "Joined T.I.I.N.A.", detail: "Appointed as T.I.I.N.A.'s Advisor (Brazil), strengthening ties between Aotearoa and Latin America" },
    ],
  },
];

function TimelineMilestone({ milestone, index, total }) {
  const isLast = index === total - 1;
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.35, delay: index * 0.07 }}
      className="flex gap-4 relative"
      data-testid={`milestone-${index}`}
    >
      {/* Vertical line and dot */}
      <div className="flex flex-col items-center flex-shrink-0 w-10">
        <div className="w-3 h-3 rounded-full bg-[#bb321a] border-2 border-[#bb321a] mt-1.5 z-10" />
        {!isLast && <div className="w-[2px] flex-1 bg-[#bb321a]/20 min-h-[32px]" />}
      </div>
      {/* Content */}
      <div className={`pb-6 ${isLast ? "pb-0" : ""}`}>
        <span className="inline-block text-xs font-bold text-[#bb321a] bg-[#bb321a]/10 px-2 py-0.5 rounded-full mb-1 tracking-wide">
          {milestone.year}
        </span>
        <h4 className="text-sm font-bold text-[#bb321a] leading-snug">{milestone.title}</h4>
        <p className="text-sm text-[#57534E] leading-relaxed mt-0.5">{milestone.detail}</p>
      </div>
    </motion.div>
  );
}

function AdvisorCard({ advisor, index }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="flex flex-col items-center"
      data-testid={`advisor-card-${index}`}
    >
      {/* Photo */}
      <div className="mb-6">
        <div className="w-[200px] h-[200px] rounded-full overflow-hidden border-4 border-[#bb321a] shadow-xl">
          <img
            src={advisor.photo}
            alt={advisor.name}
            className="w-full h-full object-cover"
            data-testid={`advisor-photo-${index}`}
          />
        </div>
      </div>

      {/* Name and Role */}
      <h3 className="font-['Syne'] text-2xl font-bold text-[#bb321a] text-center mb-1">{advisor.name}</h3>
      <p className="text-sm font-bold text-[#bb321a] uppercase tracking-wider text-center mb-1">{advisor.role}</p>
      {advisor.company && (
        <p className="text-sm text-[#364225] font-semibold text-center mb-6">
          {advisor.website ? (
            <a href={advisor.website} target="_blank" rel="noreferrer" className="hover:underline">{advisor.company}</a>
          ) : advisor.company}
        </p>
      )}

      {/* Bio */}
      <div className="max-w-3xl">
        <p className="text-base text-[#57534E] leading-relaxed text-center">{advisor.bio}</p>
      </div>

      {/* Expand toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-6 flex items-center gap-2 px-5 py-2 rounded-full border border-[#bb321a]/30 text-[#bb321a] text-sm font-semibold hover:bg-[#bb321a]/5 transition-colors duration-200 cursor-pointer group"
        data-testid={`advisor-timeline-toggle-${index}`}
      >
        <span>{expanded ? "Hide career milestones" : "View career milestones"}</span>
        <motion.span
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="inline-flex"
        >
          <ChevronDown size={16} />
        </motion.span>
      </button>

      {/* Timeline */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="overflow-hidden w-full max-w-xl mt-6"
          >
            <div
              className="bg-white rounded-xl border border-[#E7E5E4] p-6 shadow-sm"
              data-testid={`advisor-timeline-${index}`}
            >
              <p className="text-xs font-bold text-[#364225] uppercase tracking-[3px] mb-5">Career milestones</p>
              {advisor.milestones.map((m, mi) => (
                <TimelineMilestone key={mi} milestone={m} index={mi} total={advisor.milestones.length} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function AdvisorsSection() {
  return (
    <section id="advisors" className="relative py-20 overflow-hidden" style={{ background: "#FDFBF7" }}>
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-start gap-3 mb-2">
            <div className="h-[3px] w-[40px] bg-[#bb321a] mt-3"></div>
            <p className="text-[10px] font-bold text-[#bb321a] uppercase tracking-[4px]">People . Place . Planet</p>
          </div>
          <h2 className="font-['Syne'] text-3xl sm:text-4xl font-black text-[#bb321a] tracking-tight mb-3">Our Advisors</h2>
          <p className="text-base text-[#57534E] max-w-2xl mb-12">
            T.I.I.N.A. is supported by a network of Indigenous leaders and specialists who bring global expertise, deep relationships and practical knowledge to our work.
          </p>
        </motion.div>

        <div className="space-y-16">
          {ADVISORS.map((advisor, i) => (
            <AdvisorCard key={i} advisor={advisor} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
